import React, { useState, useEffect } from 'react';
import { MdRefresh, MdDashboard, MdWarning, MdCheckCircle } from 'react-icons/md';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { getUserAnalytics } from '../../../api/vehicleAnalytics';
import '../../../styles/VehicleDashboard.css';
import './BookRoom.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function VehicleDashboard() {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await getUserAnalytics();

      if (response?.data) {
        setAnalyticsData(response.data);
      } else {
        throw new Error(response?.message || 'Failed to load analytics data');
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err?.response?.data?.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations
  const capacityBreakdownChartData = analyticsData?.capacityBreakdown ? {
    labels: ['At Capacity', 'Over Capacity', 'Under Utilized', 'Optimal'],
    datasets: [{
      label: 'Routes',
      data: [
        analyticsData.capacityBreakdown.routesAtCapacity || 0,
        analyticsData.capacityBreakdown.routesOverCapacity || 0,
        analyticsData.capacityBreakdown.routesUnderUtilized || 0,
        analyticsData.capacityBreakdown.routesOptimal || 0
      ],
      backgroundColor: [
        'rgba(255, 206, 86, 0.6)',    // Yellow for At Capacity
        'rgba(255, 99, 132, 0.6)',    // Red for Over Capacity
        'rgba(201, 203, 207, 0.6)',   // Gray for Under Utilized
        'rgba(75, 192, 192, 0.6)',    // Green for Optimal
      ],
      borderColor: [
        'rgba(255, 206, 86, 1)',
        'rgba(255, 99, 132, 1)',
        'rgba(201, 203, 207, 1)',
        'rgba(75, 192, 192, 1)',
      ],
      borderWidth: 1,
    }]
  } : null;

  const routeCapacityChartData = analyticsData?.routeAnalytics ? {
    labels: analyticsData.routeAnalytics.map(r => r.routeName),
    datasets: [
      {
        label: 'Capacity',
        data: analyticsData.routeAnalytics.map(r => r.totalCapacity),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
      {
        label: 'Assigned Users',
        data: analyticsData.routeAnalytics.map(r => r.assignedUsers),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      }
    ]
  } : null;

  const userDistributionChartData = analyticsData?.userDistribution ? {
    labels: analyticsData.userDistribution.map(d => d.routeName),
    datasets: [{
      label: 'User Count',
      data: analyticsData.userDistribution.map(d => d.userCount),
      backgroundColor: 'rgba(153, 102, 255, 0.6)',
    }]
  } : null;

  const getCapacityStatusColor = (status) => {
    switch (status) {
      case 'OVER_CAPACITY':
        return 'status-over';
      case 'AT_CAPACITY':
        return 'status-at';
      case 'OPTIMAL':
        return 'status-optimal';
      case 'UNDER_UTILIZED':
        return 'status-under';
      default:
        return '';
    }
  };

  const getCapacityStatusLabel = (status) => {
    switch (status) {
      case 'OVER_CAPACITY':
        return (
          <span>
            Over Capacity <MdWarning style={{ display: 'inline', marginLeft: '4px', color: '#ef4444' }} />
          </span>
        );
      case 'AT_CAPACITY':
        return 'At Capacity';
      case 'OPTIMAL':
        return (
          <span>
            Optimal <MdCheckCircle style={{ display: 'inline', marginLeft: '4px', color: '#10b981' }} />
          </span>
        );
      case 'UNDER_UTILIZED':
        return 'Under Utilized';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">{error}</div>
        <button onClick={loadDashboardData} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="vehicle-dashboard">
      {/* Compact Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdDashboard className="title-icon" />
            Vehicle Analytics Dashboard
          </h2>
        </div>
        <div className="header-filters-compact">
          <button className="btn-book-compact" onClick={loadDashboardData}>
            <MdRefresh />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>

      {analyticsData && (
        <>
          {/* Summary Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <h3>Total Routes</h3>
              <p className="metric-value">{analyticsData.totalRoutes}</p>
              <div className="metric-details">
                <span>Active routes with assigned vehicles</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Total Vehicles</h3>
              <p className="metric-value">{analyticsData.totalVehicles}</p>
              <div className="metric-details">
                <span>Capacity: {analyticsData.totalCapacity} seats</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Assigned Users</h3>
              <p className="metric-value">{analyticsData.totalAssignedUsers}</p>
              <div className="metric-details">
                <span>Users assigned to routes</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Overall Utilization</h3>
              <p className="metric-value">{analyticsData.overallUtilization?.toFixed(1)}%</p>
              <div className="metric-details">
                <span className={analyticsData.overallUtilization > 100 ? 'over-capacity' : 'normal'}>
                  {analyticsData.overallUtilization > 100 ? 'Over Capacity' :
                   analyticsData.overallUtilization >= 80 ? 'High Utilization' : 'Normal'}
                </span>
              </div>
            </div>
          </div>

          {/* Capacity Overview Cards */}
          <div className="capacity-overview">
            <h2>Capacity Overview</h2>
            <div className="capacity-cards">
              <div className="capacity-card status-over">
                <h4>Over Capacity</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesOverCapacity || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-at">
                <h4>At Capacity</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesAtCapacity || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-optimal">
                <h4>Optimal</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesOptimal || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-under">
                <h4>Under Utilized</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesUnderUtilized || 0}</p>
                <span>routes</span>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            {capacityBreakdownChartData && (
              <div className="chart-card small">
                <h3>Capacity Distribution</h3>
                <Doughnut
                  data={capacityBreakdownChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                  }}
                />
              </div>
            )}

            {routeCapacityChartData && (
              <div className="chart-card">
                <h3>Route Capacity vs Assigned Users</h3>
                <Bar
                  data={routeCapacityChartData}
                  options={{
                    responsive: true,
                    scales: {
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45
                        }
                      }
                    }
                  }}
                />
              </div>
            )}

            {userDistributionChartData && (
              <div className="chart-card">
                <h3>User Distribution by Route</h3>
                <Bar
                  data={userDistributionChartData}
                  options={{
                    responsive: true,
                    indexAxis: 'y',
                  }}
                />
              </div>
            )}
          </div>

          {/* Route Details Table */}
          {analyticsData.routeAnalytics && analyticsData.routeAnalytics.length > 0 && (
            <div className="route-details-section">
              <h2>Route Details</h2>
              <div className="table-responsive">
                <table className="route-table">
                  <thead>
                    <tr>
                      <th>Route</th>
                      <th>Timing</th>
                      <th>Vehicles</th>
                      <th>Capacity</th>
                      <th>Assigned</th>
                      <th>Available</th>
                      <th>Utilization</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.routeAnalytics.map((route, index) => (
                      <tr key={index}>
                        <td><strong>{route.routeName}</strong></td>
                        <td>{route.timing}</td>
                        <td>{route.assignedVehicles?.length || 0}</td>
                        <td>{route.totalCapacity}</td>
                        <td>{route.assignedUsers}</td>
                        <td className={route.availableSeats < 0 ? 'negative' : 'positive'}>
                          {route.availableSeats}
                        </td>
                        <td>{route.utilizationPercent?.toFixed(1)}%</td>
                        <td>
                          <span className={`status-badge ${getCapacityStatusColor(route.capacityStatus)}`}>
                            {getCapacityStatusLabel(route.capacityStatus)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Users Section */}
          {analyticsData.topUsers && analyticsData.topUsers.length > 0 && (
            <div className="top-users-section">
              <h2>Assigned Users</h2>
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Employee ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Route</th>
                      <th>Vehicle</th>
                      <th>Department</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.topUsers.map((user, index) => (
                      <tr key={index}>
                        <td>{user.userId}</td>
                        <td><strong>{user.userName}</strong></td>
                        <td>{user.userEmail}</td>
                        <td>{user.assignedRoute}</td>
                        <td>{user.assignedVehicle}</td>
                        <td>{user.department}</td>
                        <td>{user.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default VehicleDashboard;
